import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowDown, ArrowUp, ChevronsUpDown, Search } from 'lucide-react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { request } from '../../Common/api/client';
import { useToast } from '../../Common/context/ToastContext';
import { getAdminRegisteredPharmacies } from '../api/pharmacy';
import { getAdminCpList } from '../api/cp';

dayjs.extend(utc);
const PAGE_SIZE = 50;

function rowsOf(data) {
  const rows = data?.rows ?? data?.list ?? data?.pharmacies ?? data?.terminals ?? (Array.isArray(data) ? data : []);
  return Array.isArray(rows) ? rows : [];
}


function normalizePharmacy(row = {}, index = 0) {
  const terminal = row.terminal ?? row.salesperson ?? row.driver ?? {};
  return {
    ...row,
    key: String(row.mappingId ?? row.mapping_id ?? row.customerMapId ?? row.customer_map_id ?? row.id ?? index),
    mappingId: row.mappingId ?? row.mapping_id ?? row.customerMapId ?? row.customer_map_id ?? '',
    pharmacyId: row.pharmacyId ?? row.pharmacy_id ?? row.id ?? '',
    code: row.externalCustomerCode ?? row.external_customer_code ?? row.institutionCode ?? row.institution_code ?? row.code ?? '',
    name: row.name ?? row.customerName ?? row.customer_name ?? row.pharmacyName ?? row.pharmacy_name ?? '',
    businessNo: row.businessNo ?? row.business_no ?? '',
    phone: row.phone ?? row.pharmacyPhone ?? row.pharmacy_phone ?? '',
    address: row.address ?? row.addr ?? '',
    emdongName: row.emdongName ?? row.emdong_nm ?? row.endong_nm ?? '',
    terminalPk: String(row.terminalPk ?? row.terminal_pk ?? row.terminal_id ?? terminal.id ?? ''),
    terminalName: row.terminalName ?? row.terminal_name ?? row.salespersonName ?? row.salesperson_name ?? row.driverName ?? row.driver_name ?? terminal.name ?? '',
    status: String(row.status ?? 'ACTIVE').toUpperCase(),
  };
}

function normalizePayment(row = {}, index = 0) {
  const rawStatus = String(row.status ?? '').toUpperCase();
  return {
    id: row.paymentTransactionId ?? row.payment_transaction_id ?? row.id ?? index,
    date: row.approvedAt ?? row.approved_at ?? row.createdAt ?? row.created_at ?? '',
    approvalNo: row.approvalNumber ?? row.appr_no ?? row.approval_no ?? '',
    cardName: row.cardName ?? row.card_nm ?? row.card_company ?? '-',
    amount: Number(row.amount ?? row.trdAmt ?? row.trd_amt ?? 0),
    status: rawStatus.includes('CANCEL') ? '취소' : (rawStatus === 'COMPLETED' || rawStatus.includes('APPROV') || rawStatus.includes('SUCCESS') ? '승인' : (row.status || '-')),
  };
}

function formatBusinessNo(value) {
  const digits = String(value ?? '').replace(/\D/g, '');
  return digits.length === 10 ? digits.replace(/^(\d{3})(\d{2})(\d{5})$/, '$1-$2-$3') : (value || '-');
}

function formatPhone(value) {
  const digits = String(value ?? '').replace(/\D/g, '');
  if (!digits) return '-';
  if (digits.startsWith('02')) return digits.replace(/^(02)(\d{3,4})(\d{4})$/, '$1-$2-$3');
  return digits.replace(/^(\d{3})(\d{3,4})(\d{4})$/, '$1-$2-$3');
}

function formatDate(value) {
  const date = dayjs.utc(value);
  return value && date.isValid() ? date.add(9, 'hour').format('YYYY.MM.DD HH:mm') : '-';
}

function SortIcon({ active, order }) {
  if (!active) return <ChevronsUpDown size={13} className="text-[#adb5bd]" />;
  return order === 'asc' ? <ArrowUp size={13} /> : <ArrowDown size={13} />;
}

export default function SingleCpPharmacyManage() {
  const { showToast } = useToast();
  const showToastRef = useRef(showToast);
  showToastRef.current = showToast;
  const [cp, setCp] = useState(null);
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [payments, setPayments] = useState([]);
  const [paymentLoading, setPaymentLoading] = useState(false);

  useEffect(() => {
    const timer = window.setTimeout(() => { setDebouncedQuery(query.trim()); setPage(1); }, 250);
    return () => window.clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    let cancelled = false;
    getAdminCpList({ page: 1, limit: 1 }).then((data) => {
      if (!cancelled) setCp(rowsOf(data)[0] ?? null);
    }).catch((error) => { if (!cancelled) showToastRef.current(error?.message || '조직 정보를 불러오지 못했습니다.', 'error'); });
    return () => { cancelled = true; };
  }, []);


  const loadPharmacies = useCallback(async () => {
    if (!cp) return;
    const cpPk = cp.id ?? cp.cpPkId ?? cp.cp_pk_id ?? cp.cp_id;
    setLoading(true);
    try {
      const params = { cpId: cpPk, page, limit: PAGE_SIZE, sortBy, sortOrder };
      if (debouncedQuery) params.q = debouncedQuery;
      const data = await getAdminRegisteredPharmacies(params);
      const next = rowsOf(data).map(normalizePharmacy);
      setRows(next);
      setTotal(Number(data?.total ?? data?.count) || next.length);
    } catch (error) {
      setRows([]);
      setTotal(0);
      showToastRef.current(error?.message || '거래처를 불러오지 못했습니다.', 'error');
    } finally { setLoading(false); }
  }, [cp, debouncedQuery, page, sortBy, sortOrder]);

  useEffect(() => { loadPharmacies(); }, [loadPharmacies]);
  useEffect(() => {
    setSelected((previous) => rows.find((row) => row.key === previous?.key) ?? rows[0] ?? null);
  }, [rows]);

  useEffect(() => {
    if (!selected) { setPayments([]); return undefined; }
    let cancelled = false;
    const load = async () => {
      setPaymentLoading(true);
      try {
        const params = { page: 1, limit: 100, q: selected.name };
        if (selected.mappingId) params.customerMapId = selected.mappingId;
        const data = await request({ method: 'GET', url: '/api/admin/payment-transactions', params });
        if (!cancelled) setPayments(rowsOf(data).map(normalizePayment));
      } catch (_) { if (!cancelled) setPayments([]); }
      finally { if (!cancelled) setPaymentLoading(false); }
    };
    load();
    return () => { cancelled = true; };
  }, [selected]);

  const changeSort = (field) => {
    setPage(1);
    if (sortBy === field) setSortOrder((value) => value === 'asc' ? 'desc' : 'asc');
    else { setSortBy(field); setSortOrder('asc'); }
  };


  const approved = useMemo(() => payments.filter((row) => row.status === '승인'), [payments]);
  const approvedAmount = approved.reduce((sum, row) => sum + row.amount, 0);
  const lastPage = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="flex-1 min-h-0 overflow-hidden bg-[#f7f8fa] p-6">
      <div className="h-full min-h-0 flex flex-col gap-3">
        <header className="shrink-0 flex items-end justify-between gap-4">
          <div><h1 className="text-[20px] font-extrabold text-[#191f28]">거래처 관리</h1><p className="mt-1 text-[13px] text-[#6b7684]">약국 정보와 Excel로 배정된 담당자, 최근 결제내역을 조회합니다.</p></div>
          <span className="text-[13px] font-semibold text-[#6b7684]">총 {total.toLocaleString('ko-KR')}곳</span>
        </header>
        <div className="shrink-0 flex items-center justify-between gap-3">
          <div className="flex h-[40px] w-[420px] max-w-full items-center gap-2 rounded-lg border border-[#e5e8eb] bg-white px-3 focus-within:border-[var(--brand-primary)]"><Search size={17} className="text-[#8b95a1]" /><input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="거래처코드, 약국명, 사업자번호 검색" className="min-w-0 flex-1 border-0 bg-transparent text-[14px] outline-none" /></div>
          <span className="text-[12px] font-semibold text-[#6b7684]">담당자 배정은 Excel 업로드에서만 변경됩니다.</span>
        </div>
        <div className="flex-1 min-h-0 flex gap-4">
          <section className="basis-[70%] min-w-0 overflow-hidden rounded-xl border border-[#e5e8eb] bg-white flex flex-col">
            <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden">
              <table className="w-full table-fixed text-left text-[12px]">
                <thead className="sticky top-0 z-10 bg-[#f9fafb] text-[#6b7684]"><tr className="h-[40px] border-b border-[#e5e8eb]">
                  <th className="w-[12%] px-2"><button type="button" onClick={() => changeSort('code')} className="flex items-center gap-1 font-bold">거래처코드<SortIcon active={sortBy === 'code'} order={sortOrder} /></button></th>
                  <th className="w-[20%] px-2"><button type="button" onClick={() => changeSort('name')} className="flex items-center gap-1 font-bold">약국명<SortIcon active={sortBy === 'name'} order={sortOrder} /></button></th>
                  <th className="w-[10%] px-2">읍면동</th><th className="w-[15%] px-2">사업자번호</th><th className="w-[14%] px-2">연락처</th><th className="w-[21%] px-2">담당자</th><th className="w-[8%] px-2 text-center">상태</th>
                </tr></thead>
                <tbody className="divide-y divide-[#f0f2f1]">
                  {loading ? <tr><td colSpan={7} className="py-14 text-center text-[#8b95a1]">거래처를 불러오는 중입니다.</td></tr> : rows.length === 0 ? <tr><td colSpan={7} className="py-14 text-center text-[#8b95a1]">조회된 거래처가 없습니다.</td></tr> : rows.map((row) => <tr key={row.key} onClick={() => setSelected(row)} className={`h-[48px] cursor-pointer ${selected?.key === row.key ? 'bg-[var(--brand-primary-soft)]' : 'hover:bg-[#f9fafb]'}`}>
                    <td className="truncate px-2 font-mono">{row.code || '-'}</td><td className="truncate px-2 font-bold text-[#191f28]" title={row.name}>{row.name || '-'}</td><td className="truncate px-2">{row.emdongName || '-'}</td><td className="truncate px-2 font-mono">{formatBusinessNo(row.businessNo)}</td><td className="truncate px-2 font-mono">{formatPhone(row.phone)}</td>
                    <td className="px-2"><span className="block truncate text-[#6b7684]">{row.terminalName || '미할당'}</span></td>
                    <td className="px-2 text-center"><span className={`rounded px-1.5 py-0.5 text-[10px] font-bold ${row.status === 'ACTIVE' ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>{row.status === 'ACTIVE' ? '활성' : '비활성'}</span></td>
                  </tr>)}
                </tbody>
              </table>
            </div>
            {total > PAGE_SIZE && <div className="shrink-0 flex items-center justify-center gap-3 border-t border-[#eef1f4] px-4 py-2.5 text-[12px]"><button type="button" disabled={page <= 1} onClick={() => setPage((v) => v - 1)} className="rounded border border-[#e5e8eb] px-3 py-1.5 disabled:opacity-40">이전</button><span>{page} / {lastPage}</span><button type="button" disabled={page >= lastPage} onClick={() => setPage((v) => v + 1)} className="rounded border border-[#e5e8eb] px-3 py-1.5 disabled:opacity-40">다음</button></div>}
          </section>

          <aside className="basis-[30%] min-w-[300px] overflow-hidden rounded-xl border border-[#e5e8eb] bg-white flex flex-col">
            {!selected ? <div className="flex h-full items-center justify-center text-[13px] text-[#8b95a1]">거래처를 선택해 주세요.</div> : <>
              <div className="shrink-0 border-b border-[#eef1f4] p-4"><p className="font-mono text-[12px] text-[var(--brand-primary)]">{selected.code || '-'}</p><h2 className="mt-1 truncate text-[18px] font-bold text-[#191f28]">{selected.name}</h2><p className="mt-2 truncate text-[12px] text-[#8b95a1]" title={selected.address}>{[selected.emdongName, selected.address].filter(Boolean).join(' · ') || '-'}</p><div className="mt-3 grid grid-cols-2 gap-2 rounded-lg bg-[#f7f8fa] p-3 text-[12px]"><div><span className="block text-[#8b95a1]">승인 건수</span><strong className="mt-1 block text-[15px]">{approved.length.toLocaleString('ko-KR')}건</strong></div><div><span className="block text-[#8b95a1]">승인 금액</span><strong className="mt-1 block text-[15px] text-[var(--brand-primary)]">{approvedAmount.toLocaleString('ko-KR')}원</strong></div></div></div>
              <div className="shrink-0 px-4 py-3 text-[13px] font-bold text-[#191f28]">최근 결제내역</div>
              <div className="flex-1 min-h-0 overflow-y-auto px-4 pb-4">{paymentLoading ? <p className="py-10 text-center text-[12px] text-[#8b95a1]">결제내역을 불러오는 중입니다.</p> : payments.length === 0 ? <p className="py-10 text-center text-[12px] text-[#8b95a1]">결제내역이 없습니다.</p> : payments.map((payment) => <div key={payment.id} className="border-b border-[#f0f2f1] py-3"><div className="flex items-center justify-between gap-2"><span className="truncate text-[12px] font-semibold text-[#191f28]">{payment.cardName}</span><strong className={payment.status === '취소' ? 'text-[13px] text-[#e5484d]' : 'text-[13px] text-[#191f28]'}>{payment.amount.toLocaleString('ko-KR')}원</strong></div><div className="mt-1 flex items-center justify-between gap-2 text-[11px] text-[#8b95a1]"><span>{formatDate(payment.date)}</span><span>{payment.status}</span></div></div>)}</div>
            </>}
          </aside>
        </div>
      </div>
    </div>
  );
}
